import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../molecules/PageHeader';
import { SettingsForm } from '../organisms/SettingsForm';
import { Toast } from '../atoms/Toast';
import { loadSettingsFromStorage, saveSettingsToStorage } from '../../utils/storage';
import { useAuth } from '../../contexts/AuthContext';
import type { GlobalSettings } from '../../types/settings';

type ToastState = { message: string; type: 'success' | 'error' } | null;

export const SettingsPage: React.FC = () => {
  const { isLoading: authLoading } = useAuth();
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<ToastState>(null);
  const closeToast = useCallback(() => setToast(null), []);

  // Wait for auth to be resolved before loading settings — avoids reading from
  // localStorage as a guest when the user is actually authenticated (race condition
  // between token restoration and page mount).
  useEffect(() => {
    if (authLoading) return;

    const loadSettings = async () => {
      try {
        const storedSettings = await loadSettingsFromStorage();
        setSettings(storedSettings);
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [authLoading]);

  const handleSettingsChange = async (newSettings: GlobalSettings) => {
    setSettings(newSettings);
    try {
      await saveSettingsToStorage(newSettings);
      setToast({ message: 'Paramètres sauvegardés avec succès.', type: 'success' });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      setToast({ message: 'Erreur lors de la sauvegarde des paramètres.', type: 'error' });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 pt-8 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement des paramètres...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Configuration"
          subtitle="Gestion des paramètres globaux, coûts énergétiques et options fiscales"
        />

        {settings && (
          <SettingsForm
            settings={settings}
            onSettingsChange={handleSettingsChange}
            isFullPage={true}
          />
        )}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
    </div>
  );
};
